'use client';
import React, { useEffect, useState } from 'react';
import '../../marvinjs/gui/lib/promise-1.0.0.min';
import '../../marvinjs/gui/gui.nocache';
import '../../marvinjs/js/marvinjslauncher';
// import '../marvinjs/gui/css/editor.min.css';
import './index.scss';
import { Button, GlobalLoading, Modal } from '@/shared/components';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import LZString from 'lz-string';
import { useStructureStore } from './store';
import { encodeXml } from './helper';
import { useStructureToNumber } from '@/hooks/structure-search.hook';
import { useRouter } from 'next/navigation';
import { usePageTracking, useEventTracking } from '@/shared/tracking/use-tracking';

interface IProps {
  className?: string;
}

const StructureSearch = (props: IProps) => {
  usePageTracking();
  const { t } = useLang(NAMESPACE.structureSearch);
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const { mrv, setMrv, svg, setSvg, setCasNumberList } = useStructureStore();
  const [marvinSketcherInstance, setMarvinSketcherInstance] = useState(null);
  const structureSearchApi = useStructureToNumber();
  const router = useRouter();
  const { trackEvent } = useEventTracking();
  const [marvinInstanceVisible, setMarvinInstanceVisible] = useState(false);
  useEffect(() => {
    const data = structureSearchApi.data;
    if (!data) return;
    if (!data?.data?.casNumberList) {
      const totalResults = data?.page?.count || 0;
      const searchStatus = data?.code === '200' && totalResults ? 'search:successful' : 'search:failure';
      const keyWord =
        data?.code === '200' && totalResults ? handleMrv(marvinSketcherInstance.exportAsMrv()) : 'chemical structure';
      const searchObj = {
        searchType: 'structure search',
        keyword: keyWord,
        totalResults: totalResults,
      };
      trackEvent({
        key: 'search',
        action: searchStatus,
        payload: searchObj,
      });
    }

    if (!data.data?.casNumberList) {
      setAlertOpen(true);
    } else {
      setCasNumberList(data.data?.casNumberList);
      if (data.data?.exactSearch) {
        router.push(`/search/search-result?query=${data.data?.casNumberList}`);
      } else {
        router.push(`/search/search-result/structure`);
      }
    }
  }, [structureSearchApi.data]);

  useEffect(() => {
    if (structureSearchApi.isLoading) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [structureSearchApi.isLoading]);

  useEffect(() => {
    const marvinjsInit = (event) => {
      if (event.data === 'sketcherReady') {
        //@ts-ignore
        MarvinJSUtil.getEditor('#structure_sketch')
          .then(function (sketcherInstance) {
            setMarvinSketcherInstance(sketcherInstance);
            if (mrv) {
              const drawing = LZString.decompress(mrv);
              sketcherInstance.importStructure(null, drawing);
            }
          })
          .catch(function (error) {
            console.error('Loading failed: ' + error);
          })
          .finally(() => {
            setMarvinInstanceVisible(true);
          });
      }
    };
    window.addEventListener('message', marvinjsInit);
    return () => {
      window.removeEventListener('message', marvinjsInit);
    };
  }, []);

  const onExactSearch = () => {
    if (!marvinInstanceVisible) return;
    if (marvinSketcherInstance && !marvinSketcherInstance.isEmpty()) {
      const mrv = handleMrv(marvinSketcherInstance.exportAsMrv());
      structureSearchApi.mutate({
        mrv: mrv,
        exactSearch: 'true',
      });
    } else {
      setOpen(true);
    }
  };
  const onSubstructureSearch = () => {
    if (!marvinInstanceVisible) return;
    if (marvinSketcherInstance && !marvinSketcherInstance.isEmpty()) {
      const mrv = handleMrv(marvinSketcherInstance.exportAsMrv());
      structureSearchApi.mutate({
        mrv: mrv,
        exactSearch: 'false',
      });
    } else {
      setOpen(true);
    }
  };

  const createSvgExporter = () => {
    const settings = {
      'background-color': '#ffffff',
      zoomMode: 'fit',
      width: 210,
      height: 210,
    };
    const params = {
      imageType: 'image/svg',
      settings: settings,
      inputFormat: 'mrv',
    };
    //@ts-ignore
    return new marvin.ImageExporter(params);
  };

  const createSvg = (mrv) => {
    const exporter = createSvgExporter();
    try {
      exporter.render(mrv).then((svg) => {
        setSvg(svg);
      }, alert);
    } catch (err) {
      console.log('error caught:', err);
    }
  };

  const handleMrv = (jsonData: string) => {
    const rawData = marvinSketcherInstance.exportAsMrv();
    createSvg(rawData);
    const mrv = LZString.compress(marvinSketcherInstance.exportAsMrv());
    setMrv(mrv);
    return encodeXml(jsonData);
  };

  return (
    <>
      <iframe
        id="structure_sketch"
        src="/apac/marvinjs"
        className={`h-[600px] structure-sketch-container border border-solid border-gray-450 ${props.className ?? ''}`}
      />
      <div className="flex sm:flex-col sm:gap-y-4 justify-end gap-x-8 pr-8 mt-6">
        <Button type="outline" onClick={onExactSearch} disabled={!marvinInstanceVisible}>
          {t('exact.search')}
        </Button>
        <Button type="outline" onClick={onSubstructureSearch} disabled={!marvinInstanceVisible}>
          {t('substructure.search')}
        </Button>
      </div>
      <Modal
        isOpen={open}
        customRenderFooter={<></>}
        title={t('draw.structure')}
        handleClose={() => {
          setOpen(false);
        }}
        handleCancel={() => {
          setOpen(false);
        }}
      >
        <p>{t('draw.structure.description')}</p>
      </Modal>
      <Modal
        isOpen={alertOpen}
        customRenderFooter={<></>}
        title={t('no.results')}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleCancel={() => {
          setAlertOpen(false);
        }}
      >
        <p>{t('no.results.description')}</p>
      </Modal>
    </>
  );
};

export default StructureSearch;
